/**
 * Shared column/row derivation for the bid-multiplier output payload.
 * Pulled out of BidOptimizerResults so the detail route ([id]/index.tsx)
 * can parse it as part of its single combined fetch — keeping exactly one
 * loading spinner per page view instead of two sequential ones.
 */

export interface BidOptimizerColumn {
  key: string;
  label: string;
  isBidValue: boolean;
  filterOptions: string[];
}

export interface BidOptimizerRow {
  __idx: number;
  [key: string]: any;
}

export function parseBidMultiplierOutput(records: any[]): { columns: BidOptimizerColumn[]; rows: BidOptimizerRow[] } {
  const columns = Object.keys(records[0])
    .filter(k => k.trim() !== "Google Campaign Id")
    .sort((a, b) => (a === "Bid Value" ? 1 : b === "Bid Value" ? -1 : 0))
    .map((rawKey): BidOptimizerColumn => {
      const key = rawKey.trim();
      const isBidValue = key === "Bid Value";
      const uniqueFormatted = Array.from(new Set<string>(records.map(r => {
        const v = r[rawKey];
        if (isBidValue && typeof v === "number") return v.toFixed(2);
        return typeof v === "string" ? v.trim() : String(v);
      })));
      return {
        key,
        label: isBidValue ? "Bid Multiplier" : key.replaceAll("_", " "),
        isBidValue,
        filterOptions: uniqueFormatted.sort((a, b) => isBidValue ? parseFloat(b) - parseFloat(a) : a.localeCompare(b)),
      };
    });

  const rows = records.map((item, idx): BidOptimizerRow => {
    const r: BidOptimizerRow = { __idx: idx };
    Object.keys(item).forEach(k => {
      const key = k.trim();
      const v = item[k];
      if (key === "Bid Value" && typeof v === "number") r[key] = parseFloat(v.toFixed(6));
      else r[key] = typeof v === "string" ? v.trim() : v;
    });
    return r;
  });

  return { columns, rows };
}
